import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, TextField, Stack, Alert
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import DnsRoundedIcon from '@mui/icons-material/DnsRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import { loadNetworkDiscovery, launchScan } from './networkDiscoverySlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';
import useCurrentPermissions from '../../hooks/useCurrentPermissions';

const ASSET_STATUS_COLOR = {
  active: tokens.severity.low,
  stale: tokens.severity.medium,
  offline: tokens.severity.critical
};

const JOB_STATUS_COLOR = {
  completed: tokens.severity.low,
  running: tokens.severity.info,
  queued: tokens.text.muted,
  failed: tokens.severity.critical
};

const CHANGE_EVENT_COLOR = {
  ASSET_DISCOVERED: tokens.severity.info,
  ASSET_OFFLINE: tokens.severity.critical,
  ASSET_BACK_ONLINE: tokens.severity.low,
  SERVICE_OPENED: tokens.severity.medium,
  SERVICE_CLOSED: tokens.text.muted,
  OS_CHANGED: tokens.severity.medium
};

const CHANGE_EVENT_LABEL = {
  ASSET_DISCOVERED: 'New asset',
  ASSET_OFFLINE: 'Went offline',
  ASSET_BACK_ONLINE: 'Back online',
  SERVICE_OPENED: 'Service opened',
  SERVICE_CLOSED: 'Service closed',
  OS_CHANGED: 'OS changed'
};

export default function NetworkDiscoveryPage() {
  const dispatch = useDispatch();
  const { assets, changes, jobs, status, error, scanStatus, scanError } = useSelector((s) => s.networkDiscovery);
  const { me, loaded } = useCurrentPermissions();
  const roles = new Set(me?.roles || []);
  const canLaunchScan = roles.has('SOC_ANALYST') || roles.has('ORG_ADMIN') || roles.has('SUPER_ADMIN');
  const [targetRange, setTargetRange] = useState('');

  useEffect(() => { dispatch(loadNetworkDiscovery()); }, [dispatch]);

  // Bug fix: a launched scan runs asynchronously on the backend (queued ->
  // running -> completed/failed), but nothing was ever re-fetching the job
  // list to pick up that progress — the UI just showed whatever status came
  // back from the initial POST forever, which looked exactly like a scan
  // stuck at "Queued" no matter how long you waited. Poll while any job is
  // still in a non-terminal state.
  const hasActiveJob = jobs.some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => {
    if (!hasActiveJob) return undefined;
    const id = setInterval(() => dispatch(loadNetworkDiscovery()), 4000);
    return () => clearInterval(id);
  }, [hasActiveJob, dispatch]);

  const active = assets.filter((a) => a.status === 'active').length;
  const stale = assets.filter((a) => a.status === 'stale').length;
  const offline = assets.filter((a) => a.status === 'offline').length;

  const handleLaunch = async () => {
    const range = targetRange.trim();
    if (!range || !canLaunchScan || scanStatus === 'loading') return;
    try {
      await dispatch(launchScan(range)).unwrap();
      setTargetRange('');
    } catch (_) {
      // Rejected state is rendered below; retain the CIDR so the operator can correct/retry it.
    }
  };

  return (
    <Box>
      <PageHeader
        title="Network Discovery"
        subtitle="Scheduled and on-demand CIDR-range sweeps — live asset inventory, service fingerprinting, and change tracking"
        action={<ModuleControlPanel basePath="/network-discovery" moduleLabel="Network Discovery" method="patch" />}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {scanError && <Alert severity="error" sx={{ mb: 2 }}>{scanError}</Alert>}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={3}><StatCard label="Assets Tracked" value={assets.length} icon={LanRoundedIcon} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Active" value={active} accent={tokens.severity.low} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Stale" value={stale} accent={tokens.severity.medium} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Offline" value={offline} accent={tokens.severity.critical} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Run Discovery Scan</Typography>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <TextField
                size="small" placeholder="CIDR range, e.g. 10.0.1.0/24"
                value={targetRange} onChange={(e) => setTargetRange(e.target.value)}
                disabled={!loaded || !canLaunchScan || scanStatus === 'loading'}
                helperText={loaded && !canLaunchScan ? 'SOC Analyst or administrator role is required to launch scans.' : ''}
              />
              <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} onClick={handleLaunch}
                disabled={!loaded || !canLaunchScan || scanStatus === 'loading' || !targetRange.trim()}>
                {scanStatus === 'loading' ? 'Launching…' : 'Launch Sweep'}
              </Button>
              <Typography variant="caption" sx={{ color: tokens.text.muted }}>
                Connect-scan based — no raw sockets/root required. Scoped to your own IP ranges only.
              </Typography>
            </Stack>
          </Paper>

          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Recent Scan Jobs</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Range', 'Status', 'Up', 'Started'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((j) => (
                  <TableRow key={j.id} hover>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: tokens.text.secondary }}>{j.target_range}</TableCell>
                    <TableCell>
                      <Chip label={j.status} size="small" sx={{ color: JOB_STATUS_COLOR[j.status], border: `1px solid ${JOB_STATUS_COLOR[j.status]}`, backgroundColor: 'transparent' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.76rem' }}>{j.hosts_up}/{j.hosts_scanned}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted }}>{formatRelativeTime(j.started_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ overflow: 'hidden', mb: 2.5 }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Asset Inventory</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['IP Address', 'Hostname', 'OS Guess', 'Status', 'Open Services', 'Last Seen'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.slice(0, 12).map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem', color: tokens.text.primary }}>{a.ip_address}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.secondary }}>{a.hostname || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', color: tokens.text.muted }}>{a.os_guess || 'Unknown'}</TableCell>
                    <TableCell>
                      <Chip label={a.status} size="small" sx={{ color: ASSET_STATUS_COLOR[a.status], border: `1px solid ${ASSET_STATUS_COLOR[a.status]}`, backgroundColor: 'transparent' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{a.open_service_count}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>{formatRelativeTime(a.last_seen)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 2.4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <DnsRoundedIcon fontSize="small" sx={{ color: tokens.brand.main }} />
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Change Feed</Typography>
            </Box>
            <Stack spacing={1}>
              {changes.slice(0, 10).map((c) => (
                <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, p: 1.1, borderRadius: 1.5, border: `1px solid ${tokens.border}` }}>
                  <Chip
                    label={CHANGE_EVENT_LABEL[c.event_type] || c.event_type}
                    size="small"
                    sx={{ color: CHANGE_EVENT_COLOR[c.event_type], border: `1px solid ${CHANGE_EVENT_COLOR[c.event_type]}`, backgroundColor: 'transparent', minWidth: 108 }}
                  />
                  <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.74rem', color: tokens.text.secondary }}>{c.ip_address}</Typography>
                  <Typography sx={{ fontSize: '0.76rem', color: tokens.text.muted, flexGrow: 1 }}>{c.detail}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(c.created_at)}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
